// Экран «Этажи» (структура проекта) — второй экран нового фронта (п.20, 2026-09-07).
// Колонка на корпус, этажи сплошным столбиком сверху вниз (общие уровни убраны
// 2026-09-12: дробные номера кровель плодили пустые строки у соседних корпусов);
// корпуса выровнены по земле (владелец 2026-09-12): 1-й этаж у всех на одной строке,
// высокие корпуса растут вверх, подземные этажи добирают пустыми местами снизу;
// при открытии доска прокручена к земле,
// цвет по типу этажа, приоритет в ячейке,
// карточка этажа, карточка корпуса (переименовать / этажность / удалить),
// «+ этажи», «+ корпус», мультивыбор (Ctrl/Shift или галочка) и пакетная правка.
// 2026-09-09 (владелец): показываются только корпуса, выбранные в панели фильтров
// (сама панель на этом экране оставлена только с корпусами), а что видно в ячейке
// этажа — выбирается кнопками в шапке (взаимоисключающими, владелец 2026-09-09):
// «Приоритет» (поле приоритета) или «Пуск тепла» (плановая и фактическая даты
// пуска и отметка «ТК закрыт»); повторный клик по нажатой кнопке возвращает
// простой вид — только этаж и его тип. Плановую дату держит расчёт работ теплового контура, факт и
// отметка — отчётные, на сроки не влияют. Галочки «мультивыбор» нет: несколько
// этажей выделяются Ctrl/Shift-кликом. Захватка из окон убрана 2026-09-12 (владелец:
// «добавим в следующих итерациях») — поле в базе и API живёт, значения не трогаем.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { call, p, type Data } from '../api';
import { confirmBox } from '../dialog';
import { Pop, at, type Anchor } from '../Pop';
import type { ShellCtx } from '../shell';
import { TIPY_ETAZHA, fmt, parseDate, tipColor } from '../util';

type Structure = Data<'/api/projects/{pid}/structure', 'get'>;
type Floor = Structure[string][number];

type PopState =
  | { kind: 'floor'; korpus: string; f: Floor; at: Anchor }
  | { kind: 'rabotyEt'; korpus: string; f: Floor; sek: string | null; at: Anchor }
  | { kind: 'building'; korpus: string; at: Anchor }
  | { kind: 'dopFloor'; korpus: string; at: Anchor }
  | { kind: 'newBuilding'; at: Anchor }
  | { kind: 'bulk'; at: Anchor }
  | { kind: 'prioGruppy'; at: Anchor }
  | null;

/** этажи корпуса сверху вниз: больший номер выше, безномерные — на самый верх */
/** «8 работ», «1 работа», «3 работы» (#29) */
function rabot(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  const slovo = m100 >= 11 && m100 <= 14 ? 'работ' : m10 === 1 ? 'работа' : m10 >= 2 && m10 <= 4 ? 'работы' : 'работ';
  return n + ' ' + slovo;
}

/** этаж заведён вручную (доп. этаж): метка не совпадает с номером или номера нет —
    только у таких есть смысл менять «Положение» (#29) */
function ruchnoyEtazh(f: Floor): boolean {
  return f.num == null || String(f.num) !== String(f.etazh).trim();
}

/** Подземные этажи (паркинг, подвалы), 2026-09-09: сколько минусовых этажей
    завести. Принимаем и «3», и «-3» — оба значат этажи −1, −2, −3. */
const TIP_PODZEM = 'Ниже 0,000';
function podzemnyh(v: string): number | null {
  const t = v.trim();
  if (t === '') return 0;
  const n = Math.abs(parseInt(t, 10));
  return Number.isInteger(n) && n >= 1 && n <= 300 ? n : null;
}

/** что показывать в ячейке этажа: обычный вид или пуск тепла. Кнопки «Приоритет»
    больше нет (замечание владельца #22, 2026-09-12): приоритет вводится в окне этажа
    и показывается в ячейке сам, когда задан */
type Rezhim = '' | 'pusk' | 'pri' | 'proc';

const REZHIMY: { kod: Rezhim; imya: string; podskazka: string }[] = [
  // поле приоритета в плитке — по кнопке (владелец 2026-10-17: «поле убери, появление по кнопке приоритета»)
  { kod: 'pri', imya: 'Приоритет',
    podskazka: 'Показать в каждом этаже поле приоритета: больше — раньше по всем работам, меньше нуля — в конец очереди' },
  // процент готовности этажа вместо номера; клик по этажу — работы со статусами (владелец 2026-10-17)
  { kod: 'proc', imya: 'Процент готовности',
    podskazka: 'Показать в каждом этаже процент готовности по факту; клик по этажу — список работ со статусами' },
  { kod: 'pusk', imya: 'Пуск тепла',
    podskazka: 'Показать в каждом этаже даты закрытия теплового контура и пуска тепла с отметками «случилось»' },
];

function loadRezhim(): Rezhim {
  try {
    const v = localStorage.getItem('plot_struct_rezhim');
    if (v !== null) return v === 'pusk' || v === 'pri' || v === 'proc' ? v : '';
    // разовый перенос памяти прежних галочек (2026-09-09), чтобы вид не
    // «сбросился» на глазах; старые ключи после переноса убираем — иначе они
    // каждый раз перебивали выключенный режим
    const staryj: Rezhim = localStorage.getItem('plot_struct_pusk') === '1' ? 'pusk' : '';
    localStorage.removeItem('plot_struct_pusk');
    localStorage.removeItem('plot_struct_pri');
    localStorage.setItem('plot_struct_rezhim', staryj);
    return staryj;
  } catch { /* приватный режим */ }
  return '';
}
function saveRezhim(r: Rezhim) {
  try { localStorage.setItem('plot_struct_rezhim', r); } catch { /* приватный режим */ }
}

export function Struct({ ctx }: { ctx: ShellCtx }) {
  const [struct, setStruct] = useState<Structure>({});
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [selLast, setSelLast] = useState<{ k: string; id: number } | null>(null);
  const [rezhim, setRezhim] = useState<Rezhim>(loadRezhim);
  const puskMode = rezhim === 'pusk';
  const priMode = rezhim === 'pri';
  const procMode = rezhim === 'proc';
  // готовность этажей (2026-10-17): среднее по задачам этажа — «окончены/приняты» 100, начатые — их процент,
  // не начатые 0; исключённые из расчёта не считаются. Ключ — «корпус|этаж»
  const [gotovSum, setGotovSum] = useState<Map<string, { s: number; n: number; g: number }> | null>(null);
  /** готовность этажа; у секции — её задачи плюс общие задачи этажа (без секции) */
  const gotovEt = (k: string, etazh: string, sek: string | null) => {
    if (!gotovSum) return undefined;
    const a = gotovSum.get(k + '|' + etazh + '|'), b = sek ? gotovSum.get(k + '|' + etazh + '|' + sek) : undefined;
    const n = (a?.n || 0) + (b?.n || 0);
    if (!n) return undefined;
    return { pr: Math.round(((a?.s || 0) + (b?.s || 0)) / n), vsego: n, gotovo: (a?.g || 0) + (b?.g || 0) };
  };
  const gotov = gotovSum;
  useEffect(() => {
    if (!procMode) return;
    let zhiv = true;
    void call<Data<'/api/projects/{pid}/tasks', 'get'>>('get', p('/api/projects/{pid}/tasks', { pid: ctx.pid }) + '?korpus=*&limit=20000')
      .then(r => {
        if (!zhiv) return;
        if (!r.ok) { fail(r.error.message); return; }
        const sum = new Map<string, { s: number; n: number; g: number }>();
        r.data.rows.forEach(t => {
          if (t.iskl || t.etazh == null) return;
          const kl = (t.korpus || '') + '|' + t.etazh + '|' + (t.sek || '');
          const done = t.status === 'СМР окончены' || t.status === 'СМР приняты';
          const x = sum.get(kl) || { s: 0, n: 0, g: 0 };
          x.s += done ? 100 : Math.max(0, Math.min(100, Number(t.proc) || 0)); x.n++; if (done) x.g++;
          sum.set(kl, x);
        });
        setGotovSum(sum);
      });
    return () => { zhiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procMode, ctx.pid, ctx.reloadToken]);
  const pereklyuchi = (r: Rezhim) => { const next = rezhim === r ? '' : r; setRezhim(next); saveRezhim(next); };
  // приоритет: «Все работы» (общий приоритет этажа) или отдельная группа работ
  // (#66, владелец 2026-09-18: порядок этажей у кладки и у отделки разный)
  // выбор «чей приоритет в плитках» убран с экрана (владелец 2026-10-05: «фильтр Приоритет
  // убери») — плитки всегда показывают и правят ОБЩИЙ приоритет этажа. Сохранённый раньше
  // выбор группы (plot_struct_prio_gruppa) намеренно не читаем: иначе плитки незаметно
  // правили бы приоритет группы. Уже заданные приоритеты групп в расчёте действуют как были.
  const gruppa = '';
  const [zagruzheno, setZagruzheno] = useState(false);
  const [msg, setMsg] = useState('');
  const [pop, setPop] = useState<PopState>(null);
  const can = ctx.canPlan;
  const doska = useRef<HTMLDivElement>(null);
  const [shirina, setShirina] = useState(0);   // ширина доски — от неё ширина плитки (2026-10-17)
  // фильтр по типам этажей (владелец, #24 2026-09-12): пусто = показывать все
  const [tipy, setTipyState] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('plot_struct_tipy') || '[]') as string[]; } catch { return []; }
  });
  const setTipy = (v: string[]) => {
    setTipyState(v);
    try { localStorage.setItem('plot_struct_tipy', JSON.stringify(v)); } catch { /* приватный режим */ }
  };
  const tipPodhodit = (f: Floor) => !tipy.length || tipy.includes(f.tip || '');

  // секции корпусов (#70): корпус с секциями — по строке на секцию (владелец 2026-10-17)
  const [sekcii, setSekcii] = useState<Record<string, string[]>>({});
  const load = useCallback(async () => {
    const [s, sk] = await Promise.all([
      call<Structure>('get', p('/api/projects/{pid}/structure', { pid: ctx.pid })),
      call<Record<string, string[]>>('get', p('/api/projects/{pid}/sekcii', { pid: ctx.pid })),
    ]);
    if (!s.ok) { setMsg(s.error.message); return; }
    if (sk.ok) setSekcii(sk.data);
    setStruct(s.data); setZagruzheno(true);
    const alive = new Set<number>();
    Object.values(s.data).forEach(fs => fs.forEach(f => alive.add(f.id)));
    setSel(prev => new Set([...prev].filter(id => alive.has(id))));
  }, [ctx.pid]);
  useEffect(() => { void load(); }, [load]);

  const fail = (m: string) => { setMsg(m); ctx.onError?.(m); };
  const changed = (korpusChanged = false) => { void load(); if (korpusChanged) ctx.onChanged?.(); };

  // все корпуса проекта рядом (владелец 2026-10-05: фильтр корпусов на «Этажах» убран;
  // до этого — выбранные в панели, решение 2026-09-09). Выбор корпусов с других экранов
  // здесь не действует — иначе «невидимый» фильтр прятал бы корпуса без кнопки вернуть
  const korpusa = useMemo(
    () => Object.keys(struct)
      .sort((a, b) => a.localeCompare(b, 'ru', { numeric: true })),   // К1, К2 … К10 (2026-09-12)
    [struct]);


  // общие уровни (владелец, #28 2026-09-13; отменяет «сплошные столбики» от 12.09):
  // этажи с одинаковым номером у разных корпусов стоят в одной строке, у корпуса без
  // такого номера — пустое место. Без номера — своя строка сверху по метке.
  // Земля выравнивается сама: у всех корпусов 1-й этаж на одном уровне
  const urovni = useMemo(() => {
    const nums = new Set<number>(), bez = new Set<string>();
    korpusa.forEach(k => (struct[k] || []).filter(tipPodhodit).forEach(f => {
      if (f.num != null) nums.add(f.num); else bez.add(String(f.etazh));
    }));
    const verh = [...bez].sort((a, b) => b.localeCompare(a, 'ru', { numeric: true })).map(x => 'l:' + x);
    const chisla = [...nums].sort((a, b) => b - a).map(n => 'n:' + n);
    return [...verh, ...chisla];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [struct, korpusa, tipy]);
  const klyuch = (f: Floor) => (f.num != null ? 'n:' + f.num : 'l:' + String(f.etazh));
  // этажи горизонтально (владелец 2026-10-17): корпус — строка, этажи слева направо снизу вверх;
  // подземные и 1-й — в начале строки, прокрутка к ним не нужна
  const urovniGor = useMemo(() => [...urovni].reverse(), [urovni]);
  // плитка — только номер (владелец 2026-10-17: «убери текст кроме цифры, 50 этажей на одном экране»):
  // ширина делит доску между уровнями, от 16 до 76 px. Доп. этажи (кровля, технический: дробный номер
  // или метка) — столбец в полширины: у разных корпусов кровля на разной высоте, и полные столбцы
  // под каждую съедали экран. В «Приоритете» и «Пуске тепла» — своя ширина под поля (CSS)
  // технический этаж — полный столбец с «Тех» (владелец 2026-10-17); узкие — кровля и прочие доп.
  const tehUrovni = useMemo(() => {
    const t = new Set<string>();
    korpusa.forEach(k => (struct[k] || []).forEach(f => { if (String(f.etazh).trim().toLowerCase().startsWith('тех')) t.add(klyuch(f)); }));
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [struct, korpusa]);
  const dopUroven = (u: string) => !tehUrovni.has(u) && (u.startsWith('l:') || !Number.isInteger(Number(u.slice(2))));
  const estSek = korpusa.some(k => (sekcii[k] || []).length > 0);
  const SHAPKA_W = 64 + (estSek ? 24 : 0), DOP_W = 72, ZAZOR = 2;   // DOP_W — столбец «Доп. этажи» с отступом
  const nDop = urovniGor.filter(dopUroven).length, nCel = urovniGor.length - nDop;
  const plitkaW = Math.max(16, Math.min(76, Math.floor(
    (shirina - SHAPKA_W - DOP_W - 12 - urovniGor.length * ZAZOR) / Math.max(1, nCel + nDop / 2))));
  const dopW = Math.max(12, Math.round(plitkaW / 2));
  const uzkie = !puskMode;   // в «Приоритете» ширина та же, поле — под номером (владелец 2026-10-17)
  const shirStil = (u: string) => (uzkie ? { flexBasis: dopUroven(u) ? dopW : plitkaW, width: dopUroven(u) ? dopW : plitkaW } : {});

  // доска — на весь остаток окна (2026-10-05): страница целиком не прокручивается, высокий
  // корпус прокручивается внутри доски. Плитка фиксированная, 32 px (владелец 2026-10-05:
  // «зафиксируем размер плитки, немного поменьше чем была» — была 40 px); автоподгонку
  // высоты под число этажей пробовали в тот же день и отказались — при 50 этажах мелко
  // гнездо в верхней строке фильтров (Filters.tsx рисует его раньше экрана, но в DOM оно
  // появляется вместе с экраном — поэтому ищем после первого кадра)
  const [gnezdo, setGnezdo] = useState<HTMLElement | null>(null);
  useEffect(() => { setGnezdo(document.getElementById('fslot')); }, []);
  const [vysota, setVysota] = useState(0);
  const podognat = useCallback(() => {
    const el = doska.current;
    if (!el) return;
    // 14 px — нижний отступ .main, ещё 4 — чтобы не появлялась прокрутка страницы
    const v = Math.max(240, Math.floor(window.innerHeight - el.getBoundingClientRect().top - 18));
    setVysota(x => (x === v ? x : v));
    const w = el.clientWidth;
    setShirina(x => (x === w ? x : w));
  }, []);
  useLayoutEffect(() => { podognat(); }, [podognat, zagruzheno, korpusa.length, gnezdo]);
  useEffect(() => {
    window.addEventListener('resize', podognat);
    return () => window.removeEventListener('resize', podognat);
  }, [podognat]);

  async function patchFloor(f: Floor, patch: Record<string, unknown>) {
    const r = await call('patch', p('/api/floors/{fid}', { fid: f.id }), patch);
    if (!r.ok) { fail(r.error.message); changed(); return; }
    setMsg(''); changed();
  }
  const setPrior = (f: Floor, v: string) => (gruppa
    ? setPriorGruppy([f.id], v)
    : patchFloor(f, { avto_prioritet: v.trim() || null }));

  /** приоритет для выбранной группы работ: пусто снимает исключение — этаж снова
   *  идёт по общему приоритету (#66) */
  async function setPriorGruppy(ids: number[], v: string) {
    const t = v.trim();
    const r = await call<{ etazhej: number }>('put', p('/api/projects/{pid}/floors/prioritety', { pid: ctx.pid }),
      { ids, gruppa, prioritet: t === '' ? null : t });
    if (!r.ok) { fail(r.error.message); changed(); return; }
    setMsg(t === '' ? 'Приоритет группы снят: этажей ' + r.data.etazhej + '. Нажмите «Пересчитать».'
      : 'Приоритет группы «' + gruppa + '» задан: этажей ' + r.data.etazhej + '. Нажмите «Пересчитать».');
    changed();
  }
  function cellClick(k: string, f: Floor, e: React.MouseEvent, sek: string | null = null) {
    if ((e.target as HTMLElement).closest('input.sb-pri, .sb-pusk')) return;
    if (!can) return;
    const multi = e.ctrlKey || e.metaKey || e.shiftKey;
    if (!multi) {
      setPop(procMode ? { kind: 'rabotyEt', korpus: k, f, sek, at: at(e) } : { kind: 'floor', korpus: k, f, at: at(e) });
      return;
    }
    const next = new Set(sel);
    if (e.shiftKey && selLast && selLast.k === k) {
      const arr = struct[k] || [];
      const i1 = arr.findIndex(x => x.id === selLast.id), i2 = arr.findIndex(x => x.id === f.id);
      if (i1 >= 0 && i2 >= 0) for (let j = Math.min(i1, i2); j <= Math.max(i1, i2); j++) next.add(arr[j].id);
    } else if (next.has(f.id)) next.delete(f.id); else next.add(f.id);
    setSel(next); setSelLast({ k, id: f.id });
  }

  const SPRAVKA = 'Клик по этажу — свойства; Ctrl или Shift при клике — выделить несколько.\n'
    + 'Корпус — строка, этажи идут слева направо снизу вверх; одинаковые этажи корпусов — друг под другом.\n'
    + 'Приоритет — кнопка «Приоритет» показывает поле в каждой плитке: больше — раньше по всем работам, '
    + 'меньше нуля — в конец очереди.\n'
    + 'Тип «Исключение» — этаж вне расчёта.\n'
    + 'Название корпуса — кнопка: этажность, подземные этажи, переименование, удаление.\n'
    + 'Доп. этажи (кровля, технический) стоят по номеру: «над этажом 1» = 1,5. Этаж удаляется в его окне, если на нём нет задач.';
  const panel = (
    <span className="fslot-in">
      {REZHIMY.map(r => (
        <button key={r.kod} className={'sec pv-btn' + (rezhim === r.kod ? ' on' : '')}
          title={r.podskazka + '. Клик по нажатой — вернуть простой вид'}
          onClick={() => pereklyuchi(r.kod)}>{r.imya}</button>
      ))}
      {/* тип этажа — выпадающим списком (был ряд кнопок, #24); одно значение или все */}
      <select value={tipy.length === 1 ? tipy[0] : ''} title="Показать только этажи этого типа"
        onChange={e => setTipy(e.target.value ? [e.target.value] : [])}>
        <option value="">Тип этажа: все</option>
        {TIPY_ETAZHA.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
      {sel.size > 0 && <button onClick={e => setPop({ kind: 'bulk', at: at(e) })}>Свойства выбранных ({sel.size})</button>}
      {sel.size > 0 && <button className="sec" title="Снять выделение"
        onClick={() => { setSel(new Set()); setSelLast(null); }}>✕ выделение</button>}
      {msg && <span className="pw-muted fslot-msg" title={msg}>{msg}</span>}
      {/* подсказка снизу экрана убрана (владелец 2026-10-05) — тот же текст по наведению */}
      <span className="spr-ico" title={SPRAVKA}>?</span>
    </span>
  );

  return (
    <div>
      {/* панель экрана — в верхней строке фильтров, одной строкой с корпусами (владелец
          2026-10-05: «этот блок убери, пуск тепла наверх, тип этажа выпадающим списком,
          все фильтры в одной строке»). Гнездо #fslot рисует Filters.tsx; пока его нет
          (первый кадр) — панель стоит на старом месте */}
      {gnezdo ? createPortal(panel, gnezdo) : <div className="bar actbar">{panel}</div>}
      <div className={'sb-wrap gor' + (puskMode ? ' pusk' : '') + (priMode ? ' pri' : '')} ref={doska}
        style={{ overflow: 'auto', height: vysota || '78vh', padding: '6px 2px',
        }}>
        {/* подпись над столбцом «+» (владелец 2026-10-17) */}
        {can && korpusa.length > 0 && <div className="sb-zag"><span title="«+ этаж» — добавить корпусу доп. этаж: кровлю, технический">Доп. этажи</span></div>}
        {korpusa.map(k => {
          const poUrovnyu = new Map<string, Floor>();
          (struct[k] || []).filter(tipPodhodit).forEach(f => { if (!poUrovnyu.has(klyuch(f))) poUrovnyu.set(klyuch(f), f); });
          // секции (#70): у корпуса с секциями — строка на каждую, этажи общие (секция делит этаж)
          const stroki: (string | null)[] = (sekcii[k] || []).length ? sekcii[k] : [null];
          return (
            <div className="sb-grp" key={k}>
              {/* «+ доп. этаж» — слева от корпуса (владелец 2026-10-17; был в конце строки) */}
              {can ? <div className="sb-addf" title="Добавить доп. этаж: кровля, технический этаж между жилыми — метка, тип и место «над этажом 1»"
                onClick={e => setPop({ kind: 'dopFloor', korpus: k, at: at(e) })}>+ этаж</div> : <div className="sb-addf-pust" />}
              {/* название корпуса — кнопка (владелец 2026-09-12): этажность, подземные, секции,
                  переименование, удаление; закреплено слева при прокрутке строки */}
              <div className={'sb-head' + (can ? ' knopka' : '')}
                title={can ? 'Настроить корпус: этажность, подземные этажи, секции, название, удаление' : k}
                onClick={e => { if (can) setPop({ kind: 'building', korpus: k, at: at(e) }); }}>
                <span>{k}</span>{can && <span className="sb-head-ed">▾</span>}</div>
              <div className="sb-stroki">
              {stroki.map(sek => (
              <div className="sb-row" key={sek || '-'}>
              {estSek && <span className="sb-sek" title={sek ? 'Секция ' + sek : undefined}>{sek || ''}</span>}
              {urovniGor.map(u => {
                const f = poUrovnyu.get(u);
                if (!f) return <div key={'v' + u} className="sb-void" style={shirStil(u)} />;
                const cs = tipColor(f.tip);
                const g = procMode ? gotovEt(k, String(f.etazh), sek) : undefined;
                // заливка снизу на долю готовности (поверх цвета типа)
                const zaliv = g ? { backgroundImage: 'linear-gradient(to top, var(--plot-gotovo, #86d6a4) ' + g.pr + '%, transparent ' + g.pr + '%)' } : {};
                return (
                  <div key={f.id + '|' + (sek || '')} className={'sb-cell' + (sel.has(f.id) ? ' sel' : '') + (f.vne_rascheta ? ' vne' : '')}
                    style={{ ...shirStil(u), ...(cs ? { background: cs.bg, borderTop: '4px solid ' + cs.bd } : {}), ...zaliv }}
                    title={(f.vne_rascheta ? 'ЭТАЖ ВНЕ РАСЧЁТА: все его задачи исключены из модели. ' : '')
                      + 'Этаж ' + f.etazh + (f.tip ? ' · ' + f.tip : '') + (f.zadach ? ' · ' + rabot(f.zadach) : '')
                      + (g ? ' · готовность ' + g.pr + '% (окончено ' + g.gotovo + ' из ' + g.vsego + ')' : '')
                      + (procMode ? '. Клик — работы этажа со статусами' : '. Клик — свойства этажа; Ctrl/Shift — выделение')}
                    onClick={e => cellClick(k, f, e, sek)}>
                    {puskMode ? <>
                      <span className="sb-lbl">{f.etazh}</span>
                      <PuskPolya f={f} can={can} onFail={fail} onSave={patch => patchFloor(f, patch)} />
                    </> : <>
                      {/* только номер; тип — цветом, тип и число работ — в подсказке.
                          «Процент готовности» — вместо номера процент (пусто — у этажа нет задач) */}
                      {procMode ? <span className={'sb-lbl sb-proc' + (g?.pr === 100 ? ' sb-100' : '')}>{g ? g.pr : gotov ? '' : '…'}</span> : <span className="sb-lbl">{uzkie && dopUroven(u) && dopW < 24 ? korotko(String(f.etazh)).slice(0, 1) : korotko(String(f.etazh))}</span>}
                      {/* приоритет — по кнопке «Приоритет» (2026-10-17; раньше поле было видно всегда) */}
                      {priMode && <PriInput f={f} can={can} gruppa={gruppa} onSave={v => setPrior(f, v)} />}
                    </>}
                  </div>);
              })}
              </div>))}
              </div>
            </div>
          );
        })}
        {/* «+ корпус» — плюсиком под корпусами (владелец 2026-10-17; была кнопка в строке фильтров) */}
        {can && korpusa.length > 0 && <div className="sb-row">
          <div className="sb-head knopka sb-addk" title="Добавить корпус"
            onClick={e => setPop({ kind: 'newBuilding', at: at(e) })}>+</div></div>}
        {zagruzheno && !korpusa.length && (Object.keys(struct).length
          ? <div className="pw-muted" style={{ padding: '10px 2px' }}>Выбранных корпусов в проекте нет — поменяйте выбор корпусов вверху.</div>
          // пустой проект — только плюсик «добавить корпус», как под корпусами (владелец 2026-10-17:
          // окно «Шаг 1 из 2» не показываем)
          : can && <div className="sb-row">
              <div className="sb-head knopka sb-addk" title="Добавить корпус"
                onClick={e => setPop({ kind: 'newBuilding', at: at(e) })}>+</div></div>)}
      </div>

      {pop?.kind === 'rabotyEt' && <Pop at={pop.at} width={560} onClose={() => setPop(null)}
        title={pop.korpus + ' · этаж ' + pop.f.etazh + (pop.sek ? ' · секция ' + pop.sek : '')
          + (gotovEt(pop.korpus, String(pop.f.etazh), pop.sek) ? ' · готовность ' + gotovEt(pop.korpus, String(pop.f.etazh), pop.sek)!.pr + '%' : '')}>
        <RabotyEtazha ctx={ctx} korpus={pop.korpus} etazh={String(pop.f.etazh)} sek={pop.sek} />
      </Pop>}
      {pop?.kind === 'floor' && <FloorPop ctx={ctx} pop={pop} pusk={puskMode} floors={struct[pop.korpus] || []} onClose={() => setPop(null)}
        onDone={m => { setMsg(m); setPop(null); changed(); }} onFail={fail} />}
      {pop?.kind === 'building' && <BuildingPop ctx={ctx} korpus={pop.korpus} floors={struct[pop.korpus] || []} at={pop.at}
        onClose={() => setPop(null)} onDone={m => { setMsg(m); setPop(null); changed(true); }} onFail={fail} />}
      {pop?.kind === 'dopFloor' && <DopFloorPop ctx={ctx} korpus={pop.korpus} floors={struct[pop.korpus] || []} at={pop.at}
        onClose={() => setPop(null)} onDone={m => { setMsg(m); setPop(null); changed(); }} onFail={fail} />}
      {pop?.kind === 'newBuilding' && <NewBuildingPop ctx={ctx} at={pop.at}
        onClose={() => setPop(null)} onDone={m => { setMsg(m); setPop(null); changed(true); }} onFail={fail} />}
      {pop?.kind === 'bulk' && <BulkPop ids={[...sel]} at={pop.at} onClose={() => setPop(null)}
        onDone={m => { setMsg(m); setPop(null); setSel(new Set()); setSelLast(null); changed(); }} onFail={fail} />}
      {pop?.kind === 'prioGruppy' && <PrioGruppyPop gruppa={gruppa} kolvo={sel.size} at={pop.at}
        onClose={() => setPop(null)}
        onApply={async v => { await setPriorGruppy([...sel], v); setPop(null); setSel(new Set()); setSelLast(null); }} />}
    </div>
  );
}

/** Метка этажа в узкой плитке: «Технический» → «Тех», «Кровля» → «Кр», прочие слова — три буквы */
function korotko(m: string): string {
  if (/^-?\d+([.,]\d+)?$/.test(m.trim())) return m;
  const n = m.trim().toLowerCase();
  if (n.startsWith('тех')) return 'Тех';
  if (n.startsWith('кровл')) return 'Кр';
  return m.trim().slice(0, 3);
}

/** Приоритет прямо в плитке: значение всегда из данных, при ошибке правка откатывается */
function PriInput({ f, can, gruppa, onSave }: {
  f: Floor; can: boolean; gruppa: string; onSave: (v: string) => Promise<void>;
}) {
  // выбрана группа работ — правим её приоритет (#66); пусто у группы = идёт по общему
  const znach = gruppa ? (f.prio_gruppy || {})[gruppa] ?? null : f.prioritet ?? null;
  const cur = znach != null ? String(znach) : '';
  const [v, setV] = useState(cur);
  useEffect(() => { setV(cur); }, [cur]);
  const podskazka = gruppa
    ? 'приоритет этажа для группы «' + gruppa + '»: сильнее общего; пусто — этаж идёт по общему приоритету'
      + (f.prioritet != null ? ' (сейчас ' + f.prioritet + ')' : '')
    : 'приоритет этажа: больше — раньше по всем работам, отрицательный — в конец очереди';
  return (
    <input className="sb-pri" title={podskazka}
      value={v} disabled={!can} onChange={e => setV(e.target.value)}
      style={znach != null ? { color: znach < 0 ? '#d92d20' : '#067647', fontWeight: 600 } : undefined}
      onBlur={() => { if (cur !== v.trim()) void onSave(v).then(() => setV(cur)); }}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
  );
}

/** Тепловой контур прямо в ячейке (режим «пуск тепла»; модель владельца 2026-09-12):
    два события — закрытие ТК и пуск тепла, у каждого одна дата и отметка
    «случилось»: дата без отметки — план, с отметкой — факт (поле зелёное).
    Расчёт держит дату пуска тепла, плановую или свершившуюся; закрытие ТК —
    только отчётность. Отметка при пустой дате ставит сегодняшнюю (сервер). */
function PuskPolya({ f, can, onSave, onFail }: {
  f: Floor; can: boolean; onSave: (patch: Record<string, unknown>) => Promise<void>; onFail: (m: string) => void;
}) {
  const dK = fmt(f.kontur_data) || '', dP = fmt(f.pusk) || '';
  const [k, setK] = useState(dK);
  const [pp, setPp] = useState(dP);
  useEffect(() => { setK(dK); }, [dK]);
  useEffect(() => { setPp(dP); }, [dP]);

  function save(pole: string, bylo: string, stalo: string, vernut: (v: string) => void) {
    const t = stalo.trim();
    if (t === bylo) return;
    const iso = t === '' ? null : parseDate(t);
    if (iso === undefined) { onFail('Дата не разобрана: нужно ДД.ММ.ГГГГ'); vernut(bylo); return; }
    void onSave({ [pole]: iso });
  }
  const dt = (v: string, setV: (x: string) => void, bylo: string, pole: string, podskazka: string, fakt: boolean) => (
    <input className={'sb-dt' + (fakt ? ' fakt' : '')} value={v} disabled={!can}
      title={podskazka + (fakt ? ' — случилось' : ' — план') + ' (ДД.ММ.ГГГГ)'}
      onChange={e => setV(e.target.value)}
      onBlur={() => save(pole, bylo, v, setV)}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
  );
  const chk = (checked: boolean, pole: string, podskazka: string) => (
    <input type="checkbox" className="sb-chk" checked={checked} disabled={!can} title={podskazka}
      onChange={e => void onSave({ [pole]: e.target.checked })} />
  );
  return (
    <span className="sb-pusk">
      <span className="sb-dr">
        <span className="sb-dl" title="Закрытие теплового контура: дата без отметки — план, с отметкой — факт. Только для отчётности">контур</span>
        {dt(k, setK, dK, 'kontur_data', 'Дата закрытия теплового контура', !!f.kontur_zakryt)}
        {chk(!!f.kontur_zakryt, 'kontur_zakryt', 'Контур закрыт — случилось. При пустой дате поставится сегодняшняя')}
      </span>
      <span className="sb-dr">
        <span className="sb-dl" title="Пуск тепла: дата без отметки — план, с отметкой — факт. Работы теплового контура не начнутся раньше этой даты">тепло</span>
        {dt(pp, setPp, dP, 'pusk_tepla', 'Дата пуска тепла — её держит расчёт работ ТК', !!f.pusk_bylo)}
        {chk(!!f.pusk_bylo, 'pusk_bylo', 'Тепло пущено — случилось. При пустой дате поставится сегодняшняя')}
      </span>
    </span>
  );
}

const Grid = ({ children }: { children: React.ReactNode }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 10px', margin: '10px 0', alignItems: 'center' }}>{children}</div>
);
const Sel = ({ value, opts, onChange, empty = '' }: { value: string; opts: string[]; onChange: (v: string) => void; empty?: string }) => (
  <select value={value} onChange={e => onChange(e.target.value)}>
    <option value="">{empty}</option>
    {opts.filter(Boolean).map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

type PopProps = { ctx: ShellCtx; onClose: () => void; onDone: (msg: string) => void; onFail: (m: string) => void };

type ZadachaEtazha = Data<'/api/projects/{pid}/tasks', 'get'>['rows'][number];

/** Работы этажа в окне этажа (владелец 2026-10-05: «при нажатии на этаж пусть
    отображаются работы на этом этаже, а не пуск тепла»). Сроки — по последнему пересчёту. */
function RabotyEtazha({ ctx, korpus, etazh, sek = null }: { ctx: ShellCtx; korpus: string; etazh: string; sek?: string | null }) {
  const [rows, setRows] = useState<ZadachaEtazha[] | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let zhiv = true;
    void (async () => {
      const r = await call<Data<'/api/projects/{pid}/tasks', 'get'>>('get',
        p('/api/projects/{pid}/tasks', { pid: ctx.pid }) + '?korpus=' + encodeURIComponent(korpus) + '&limit=20000');
      if (!zhiv) return;
      if (!r.ok) { setErr(r.error.message); return; }
      // у секции — её задачи и общие задачи этажа (без секции)
      setRows(r.data.rows.filter(t => String(t.etazh) === String(etazh) && (!sek || !t.sek || t.sek === sek))
        .sort((a, b) => String(a.nachalo || '9').localeCompare(String(b.nachalo || '9')) || a.rabota.localeCompare(b.rabota, 'ru')));
    })();
    return () => { zhiv = false; };
  }, [ctx.pid, korpus, etazh, sek]);
  if (err) return <div className="err">{err}</div>;
  if (!rows) return <div className="pw-muted">Загружаю работы этажа…</div>;
  if (!rows.length) return <div className="pw-muted">На этаже нет работ: на этот тип этажа ни одна работа не раскатывается.</div>;
  return (
    <div className="et-raboty">
      <div className="et-raboty-zag">Работы на этаже: {rows.length}</div>
      <div className="et-raboty-tbl"><table>
        <thead><tr><th>Работа</th><th>Начало</th><th>Окончание</th><th>Статус</th><th>%</th></tr></thead>
        <tbody>{rows.map(t => (
          <tr key={t.id} className={t.status === 'СМР окончены' ? 'gotovo' : t.status ? 'idet' : undefined}>
            <td><a title="Карточка работы" onClick={() => ctx.onWorkCard?.(t.rabota)}>{t.rabota}</a></td>
            <td className="num">{fmt(t.nachalo) || '—'}</td>
            <td className="num">{fmt(t.okonchanie) || '—'}</td>
            <td>{t.status === 'СМР окончены' ? 'окончены' : t.status === 'СМР приняты' ? 'приняты'
              : t.status === 'СМР начаты' ? 'начаты' : 'не начаты'}</td>
            <td className="num">{t.status === 'СМР окончены' || t.status === 'СМР приняты' ? 100 : t.proc != null ? t.proc : t.status ? '—' : 0}</td>
          </tr>))}</tbody>
      </table></div>
    </div>
  );
}

function FloorPop({ pop, floors, pusk, onClose, onDone, onFail }: PopProps & {
  pop: Extract<PopState, { kind: 'floor' }>; floors: Floor[];
  /** режим «Пуск тепла»: только в нём окно показывает даты теплового контура (владелец 2026-10-05) */
  pusk: boolean }) {
  const f = pop.f;
  // положение среди соседей (2026-09-12): «над этажом N» → дробный номер
  const sosedi = poNomeru(floors).filter(x => x.id !== f.id);
  const [nad, setNad] = useState('');
  const novyjNum = nad === '' ? undefined : polozhenie(sosedi, nad);
  const [v, setV] = useState({ tip: f.tip || '', zahv: f.zahvatka || '', prior: f.prioritet != null ? String(f.prioritet) : '',
    pusk: fmt(f.pusk), kont: fmt(f.kontur_data), komm: f.kommentarij || '', metka: String(f.etazh) });
  const [kz, setKz] = useState(!!f.kontur_zakryt);
  const [pb, setPb] = useState(!!f.pusk_bylo);
  const set = (k: keyof typeof v) => (val: string) => setV(s => ({ ...s, [k]: val }));
  async function save() {
    const puskIso = v.pusk.trim() === '' ? null : parseDate(v.pusk);
    if (puskIso === undefined) { onFail('Дата пуска тепла не разобрана: нужно ДД.ММ.ГГГГ'); return; }
    const kontIso = v.kont.trim() === '' ? null : parseDate(v.kont);
    if (kontIso === undefined) { onFail('Дата закрытия контура не разобрана: нужно ДД.ММ.ГГГГ'); return; }
    const body: Record<string, unknown> = {
      tip_etazha_gpr: v.tip || null, zahvatka: v.zahv.trim() || null, avto_prioritet: v.prior.trim() || null,
      kommentarij: v.komm.trim() || null };
    // тепловой контур правится только в режиме «Пуск тепла»: вне его полей в окне нет,
    // и отправлять их значения незачем
    if (pusk) Object.assign(body, { pusk_tepla: puskIso, pusk_bylo: pb, kontur_data: kontIso, kontur_zakryt: kz });
    if (novyjNum != null) body.floor_num = novyjNum;
    if (ruchnoyEtazh(f) && v.metka.trim() !== String(f.etazh)) body.floor_label = v.metka.trim();   // переименование (2026-10-17)
    const r = await call<{ vne_zadach: number; vernulos_zadach: number }>('patch', p('/api/floors/{fid}', { fid: f.id }), body);
    if (!r.ok) { onFail(r.error.message); return; }
    onDone(r.data.vne_zadach ? 'Этаж вне расчёта: исключено задач ' + r.data.vne_zadach + '. Нажмите «Пересчитать».'
      : r.data.vernulos_zadach ? 'Этаж вернулся в расчёт: задач ' + r.data.vernulos_zadach + '. Нажмите «Пересчитать».' : '');
  }
  async function del() {
    if (!confirm('Удалить этаж «' + f.etazh + '» корпуса ' + pop.korpus + '?')) return;
    const r = await call('delete', p('/api/floors/{fid}', { fid: f.id }));
    if (!r.ok) { onFail(r.error.message); return; }
    onDone('Этаж удалён.');
  }
  return (
    <Pop at={pop.at} title={pop.korpus + ' · этаж ' + f.etazh + (f.kvartir ? ' · квартир: ' + f.kvartir : '')} onClose={onClose}
      width={pusk ? 370 : 560}>
      <Grid>
        <span title="«Исключение» — этаж вне расчёта: все его задачи уходят из модели, вернуть — сменой типа обратно">Тип этажа</span>
        <Sel value={v.tip} opts={TIPY_ETAZHA} onChange={set('tip')} />
        {/* «Положение» — только у этажей, заведённых вручную (#29): у обычных номер = метка */}
        {ruchnoyEtazh(f) && (<>
          <span title="Название доп. этажа: «Тех», «Кровля»… Задачи этажа переименуются вместе с ним">Название</span>
          <input value={v.metka} maxLength={20} onChange={e => set('metka')(e.target.value)} />
          <span title="Номер для порядка: по нему этаж стоит среди соседей в структуре, шахматке и очереди «Снизу вверх»">Положение</span>
          <select value={nad} onChange={e => setNad(e.target.value)}>
            <option value="">{f.num != null ? 'номер ' + f.num + ' — не менять' : 'номера нет — задать'}</option>
            {sosedi.map(x => <option key={x.id} value={String(x.id)}>над этажом {x.etazh}</option>)}
            {sosedi.length > 0 && <option value="low">под самым нижним</option>}
          </select>
          {novyjNum != null && <><span /><span className="pw-muted">станет номер {novyjNum}</span></>}
        </>)}
        <span>Приоритет</span><input value={v.prior} placeholder="больше — раньше, минус — в конец" onChange={e => set('prior')(e.target.value)} />
        {pusk && <>
        <span title="Закрытие теплового контура — только для отчётности. Дата без отметки — план, с отметкой — факт">Закрытие ТК</span>
        <span className="sb-sob">
          <input value={v.kont} placeholder="ДД.ММ.ГГГГ" onChange={e => set('kont')(e.target.value)} />
          <label title="Случилось. При пустой дате поставится сегодняшняя"><input type="checkbox" checked={kz} onChange={e => setKz(e.target.checked)} /> готово</label>
        </span>
        <span title="Работы теплового контура не начнутся раньше этой даты. Дата без отметки — план, с отметкой — факт">Пуск тепла</span>
        <span className="sb-sob">
          <input value={v.pusk} placeholder="ДД.ММ.ГГГГ" onChange={e => set('pusk')(e.target.value)} />
          <label title="Случилось. При пустой дате поставится сегодняшняя"><input type="checkbox" checked={pb} onChange={e => setPb(e.target.checked)} /> готово</label>
        </span>
        </>}
        <span>Комментарий</span><input value={v.komm} onChange={e => set('komm')(e.target.value)} />
      </Grid>
      <div className="bar" style={{ margin: 0 }}>
        <button onClick={() => void save()}>Сохранить</button>
        <button className="del" onClick={() => void del()}>Удалить этаж</button>
      </div>
    </Pop>
  );
}

function BuildingPop({ ctx, korpus, floors, at: a, onClose, onDone, onFail }: PopProps & { korpus: string; floors: Floor[]; at: Anchor }) {
  const nums = floors.filter(f => f.num != null && f.num === Math.round(f.num) && String(Math.round(f.num)) === String(f.etazh))
    .map(f => Math.round(f.num as number)).filter(n => n > 0);
  const curN = nums.length ? Math.max(...nums) : 0;
  const [name, setName] = useState(korpus);
  const [cnt, setCnt] = useState('');
  const [pod, setPod] = useState('');
  // секции корпуса (#70, 2026-09-18): «А, Б» — этаж делится на секции, и работы
  // с раскаткой «по этажам и секциям» получают задачу на каждую
  // список секций: «+» добавляет следующую букву (владелец 2026-10-17; было поле «через запятую»)
  const [sekList, setSekList] = useState<string[]>([]);
  const [sekBylo, setSekBylo] = useState('');
  const sek = sekList.map(x => x.trim()).filter(Boolean).join(', ');
  const BUKVY = 'АБВГДЕЖЗИКЛМНОПРСТУФХЦЧШЭЮЯ';
  const sledSek = () => [...BUKVY].find(b => !sekList.some(x => x.trim().toUpperCase() === b)) || String(sekList.length + 1);
  useEffect(() => {
    void (async () => {
      const r = await call<Record<string, string[]>>('get', p('/api/projects/{pid}/sekcii', { pid: ctx.pid }));
      const t = r.ok ? (r.data[korpus] || []) : [];
      setSekList(t); setSekBylo(t.join(', '));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [korpus, ctx.pid]);
  const podEst = floors.filter(f => f.num != null && (f.num as number) < 0).length;
  async function save() {
    const msgs: string[] = [];
    // подземные этажи только досоздаются (удалять их через этажность нельзя —
    // «Этажность» считает наземные 1…N)
    const np = podzemnyh(pod);
    if (np === null) { onFail('Подземных этажей — целое число от 1 до 300.'); return; }
    if (np) {
      const rp = await call<{ sozdano: number; uzhe_byli: number }>('post',
        p('/api/projects/{pid}/floors/bulk', { pid: ctx.pid }),
        { korpus, ot: -np, do: -1, tip_etazha: TIP_PODZEM });
      if (!rp.ok) { onFail(rp.error.message); return; }
      if (rp.data.sozdano) msgs.push('добавлено подземных: ' + rp.data.sozdano);
    }
    if (sek.trim() !== sekBylo.trim()) {
      const spisok = sek.split(',').map(x => x.trim()).filter(Boolean);
      const rs = await call<{ sozdano_zadach: number; udaleno_zadach: number }>('put',
        p('/api/projects/{pid}/sekcii', { pid: ctx.pid }), { korpus, sekcii: spisok });
      if (!rs.ok) { onFail(rs.error.message); return; }
      msgs.push('секции: ' + (spisok.join(', ') || 'убраны')
        + (rs.data.sozdano_zadach ? ', создано задач ' + rs.data.sozdano_zadach : '')
        + (rs.data.udaleno_zadach ? ', убрано задач ' + rs.data.udaleno_zadach : ''));
    }
    if (cnt.trim() !== '') {
      const n = parseInt(cnt, 10);
      if (!Number.isInteger(n) || n < 1 || n > 300) { onFail('Этажность — целое число от 1 до 300.'); return; }
      if (n !== curN) {
        // лишние этажи уходят вместе с пустыми задачами; задачи с фактом или правками — после вопроса (2026-10-17)
        const url = p('/api/projects/{pid}/buildings/resize', { pid: ctx.pid });
        type Res = { sozdano: number; udaleno: number; udaleno_zadach: number };
        let r = await call<Res>('post', url, { korpus, etazhnost: n });
        if (!r.ok && r.error.code === 'nepustye') {
          if (!(await confirmBox(r.error.message))) return;
          r = await call<Res>('post', url, { korpus, etazhnost: n, force: true });
        }
        if (!r.ok) { onFail(r.error.message); return; }
        if (r.data.sozdano) msgs.push('добавлено этажей: ' + r.data.sozdano);
        if (r.data.udaleno) msgs.push('удалено этажей сверху: ' + r.data.udaleno
          + (r.data.udaleno_zadach ? ' (с их задачами: ' + r.data.udaleno_zadach + ')' : ''));
      }
    }
    const nn = name.trim();
    if (nn && nn !== korpus) {
      const r = await call('patch', p('/api/projects/{pid}/buildings', { pid: ctx.pid }), { name: korpus, new_name: nn });
      if (!r.ok) { onFail(r.error.message); return; }
      msgs.push('переименован в «' + nn + '»');
    }
    onDone(msgs.length ? 'Корпус ' + korpus + ': ' + msgs.join(', ') + '.' : 'Изменений нет.');
  }
  async function del() {
    if (!(await confirmBox('Удалить корпус «' + korpus + '» со всеми его этажами и задачами? Отменить будет нельзя.'))) return;
    const url = p('/api/projects/{pid}/buildings', { pid: ctx.pid }) + '?name=' + encodeURIComponent(korpus);
    let r = await call<{ etazhej: number; zadach?: number }>('delete', url);
    // задачи с фактом или ручными правками — отдельный вопрос (2026-10-16)
    if (!r.ok && r.error.code === 'nepustye') {
      if (!(await confirmBox(r.error.message))) return;
      r = await call<{ etazhej: number; zadach?: number }>('delete', url + '&force=1');
    }
    if (!r.ok) { onFail(r.error.message); return; }
    onDone('Корпус «' + korpus + '» удалён' + (r.data.etazhej ? ', этажей: ' + r.data.etazhej : '') + (r.data.zadach ? ', задач: ' + r.data.zadach : '') + '.');
  }
  return (
    <Pop at={a} title={'Корпус ' + korpus} onClose={onClose} width={370}>
      <Grid>
        <span>Название</span><input value={name} onChange={e => setName(e.target.value)} />
        <span>Этажность</span><input value={cnt} placeholder={'сейчас: ' + curN} onChange={e => setCnt(e.target.value)} />
        <span title="Этажи −1, −2, −3 — паркинг и подвалы">Подземных этажей</span>
        <input value={pod} placeholder={'сейчас: ' + podEst} onChange={e => setPod(e.target.value)} />
        <span title="Секции внутри этажа. Задачи по секциям получают только работы с раскаткой «по этажам и секциям»">Секции</span>
        <span className="sek-spisok">
          {sekList.map((x, i) => (
            <span className="sek-chip" key={i}>
              <input value={x} maxLength={12} title="Название секции"
                onChange={e => setSekList(l => l.map((y, j) => (j === i ? e.target.value : y)))} />
              <a title="Убрать секцию" onClick={() => setSekList(l => l.filter((_y, j) => j !== i))}>×</a>
            </span>))}
          <button type="button" className="sec sek-plus" title="Добавить секцию"
            onClick={() => setSekList(l => [...l, sledSek()])}>+</button>
        </span>
      </Grid>
      <div className="pw-muted" style={{ margin: '0 0 8px' }}>Секции делят этаж: у работ с раскаткой «по этажам и секциям»
        появится задача на каждую секцию, остальные работы останутся одной задачей на этаж. Убрать секцию можно,
        пока по её задачам нет факта и ручных правок.</div>
      <div className="pw-muted" style={{ margin: '0 0 8px' }}>Этажность — число номерных этажей 1…N: недостающие досоздадутся,
        лишние сверху удалятся вместе с их задачами (если среди них есть задачи с фактом или ручными правками — спросим). Подземные (−1, −2, −3) только досоздаются, с типом «{TIP_PODZEM}»;
        лишний подземный этаж удаляется кликом по нему. Особые этажи (Тех.этаж, кровля) не трогаются.</div>
      <div className="bar" style={{ margin: 0 }}>
        <button onClick={() => void save()}>Сохранить</button>
        <button className="del" onClick={() => void del()}>Удалить корпус</button>
      </div>
    </Pop>
  );
}

/** этажи корпуса с номером, сверху вниз */
function poNomeru(floors: Floor[]): Floor[] {
  return floors.filter(f => f.num != null).sort((x, y) => (y.num as number) - (x.num as number));
}

/** Положение «над этажом» → номер (2026-09-12): середина между выбранным этажом и
    следующим сверху; над самым верхним — +0,5 (кровля над 48-м = 48,5);
    «под самым нижним» — −0,5. `sosedi` — этажи корпуса с номером, сверху вниз. */
function polozhenie(sosedi: Floor[], nad: string): number | null {
  if (!sosedi.length) return null;
  if (nad === 'low') return Math.round(((sosedi[sosedi.length - 1].num as number) - 0.5) * 1000) / 1000;
  const i = sosedi.findIndex(x => String(x.id) === nad);
  if (i < 0) return null;
  const n = sosedi[i].num as number;
  const verh = i > 0 ? (sosedi[i - 1].num as number) : null;
  return Math.round((verh == null ? n + 0.5 : (n + verh) / 2) * 1000) / 1000;
}

/** Доп. этаж (решение владельца 2026-09-12): кровля, технический этаж между жилыми
    и т.п. Метка свободная («1т», «К»), тип из системного набора, а место задаётся
    словами — «над этажом 1» — и превращается в дробный номер (1,5): по нему этаж
    встаёт между соседями в структуре, шахматке и очереди «Снизу вверх». Поэтажные
    типовые работы на такие этажи не ложатся; свой набор работ — раскаткой
    «на технические этажи» (шаг 4) или ручным вводом. */
function DopFloorPop({ ctx, korpus, floors, at: a, onClose, onDone, onFail }: PopProps & { korpus: string; floors: Floor[]; at: Anchor }) {
  const sosedi = poNomeru(floors);
  const [v, setV] = useState({ label: '', tip: 'Технический', nad: sosedi[0] ? String(sosedi[0].id) : 'low', zahv: '' });
  const set = (k: keyof typeof v) => (val: string) => setV(s => ({ ...s, [k]: val }));
  const num = polozhenie(sosedi, v.nad);
  async function add() {
    const label = v.label.trim();
    if (!label) { onFail('Введите метку этажа — например «1т» или «К».'); return; }
    if (num == null) { onFail('В корпусе нет этажей с номерами — сначала заведите этажи.'); return; }
    const r = await call<{ sozdano: number; uzhe_byli: number }>('post', p('/api/projects/{pid}/floors/bulk', { pid: ctx.pid }),
      { korpus, label, num, tip_etazha: v.tip, zahvatka: v.zahv.trim() });
    if (!r.ok) { onFail(r.error.message); return; }
    onDone(r.data.sozdano ? 'Доп. этаж «' + label + '» добавлен, номер ' + num + '.' : 'Этаж «' + label + '» уже есть.');
  }
  return (
    <Pop at={a} title={'Доп. этаж в корпус ' + korpus} onClose={onClose} width={380}>
      <div className="pw-muted" style={{ margin: '4px 0 8px' }}>Кровля, технический этаж между жилыми и т.п.
        Поэтажные типовые работы на него не ложатся; на технические этажи ложатся работы с раскаткой
        «по техническим этажам». Место задаётся словами, номер для порядка считается сам.</div>
      <Grid>
        <span>Метка</span><input autoFocus placeholder="напр. 1т или К" value={v.label} onChange={e => set('label')(e.target.value)} />
        <span>Тип</span>
        <select value={v.tip} onChange={e => set('tip')(e.target.value)}>
          {TIPY_ETAZHA.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <span>Положение</span>
        <select value={v.nad} onChange={e => set('nad')(e.target.value)}>
          {sosedi.map(x => <option key={x.id} value={String(x.id)}>над этажом {x.etazh}</option>)}
          <option value="low">под самым нижним</option>
        </select>
        <span /><span className="pw-muted">номер для порядка: {num ?? '—'}</span>
      </Grid>
      <div className="bar" style={{ margin: 0 }}><button onClick={() => void add()}>Добавить</button></div>
    </Pop>
  );
}

function NewBuildingPop({ ctx, at: a, onClose, onDone, onFail }: PopProps & { at: Anchor }) {
  const [name, setName] = useState('');
  const [floors, setFloors] = useState('');
  const [pod, setPod] = useState('');
  async function create() {
    const nm = name.trim();
    if (!nm) { onFail('Введите название корпуса.'); return; }
    // «Этажность» с минусом — это подземные этажи (так его и вводят для паркинга)
    const naz = floors.trim();
    const minus = naz.startsWith('-');
    const n = naz === '' || minus ? 0 : parseInt(naz, 10);
    if (naz !== '' && !minus && (!Number.isInteger(n) || n < 1 || n > 300)) {
      onFail('Этажность — целое число от 1 до 300 (подземные этажи — в поле «Подземных этажей»).'); return;
    }
    const np = podzemnyh(minus ? naz : pod);
    if (np === null) { onFail('Подземных этажей — целое число от 1 до 300.'); return; }
    const r = await call('post', p('/api/projects/{pid}/buildings', { pid: ctx.pid }), { name: nm });
    if (!r.ok) { onFail(r.error.message); return; }
    let t = 'Корпус «' + nm + '» создан';
    if (n) {
      const rf = await call<{ sozdano: number }>('post', p('/api/projects/{pid}/floors/bulk', { pid: ctx.pid }), { korpus: nm, ot: 1, do: n });
      t += rf.ok ? ', этажей: ' + rf.data.sozdano : ', но этажи не создались: ' + rf.error.message;
      // кровля — сама, над верхним этажом (владелец 2026-10-17: «при добавлении корпусов ставь кровлю в конце»)
      if (rf.ok) {
        const rk = await call<{ sozdano: number }>('post', p('/api/projects/{pid}/floors/bulk', { pid: ctx.pid }),
          { korpus: nm, label: 'К', num: n + 0.5, tip_etazha: 'Кровля' });
        t += rk.ok ? ', кровля' : ', но кровля не создалась: ' + rk.error.message;
      }
    }
    if (np) {
      const rp = await call<{ sozdano: number }>('post', p('/api/projects/{pid}/floors/bulk', { pid: ctx.pid }),
        { korpus: nm, ot: -np, do: -1, tip_etazha: TIP_PODZEM });
      t += rp.ok ? ', подземных: ' + rp.data.sozdano : ', но подземные не создались: ' + rp.error.message;
    }
    if (!n && !np) t += ' — добавьте этажи плюсиком под ним';
    onDone(t + '.');
  }
  return (
    <Pop at={a} title="Новый корпус" onClose={onClose} width={360}>
      <Grid>
        <span>Название</span><input autoFocus placeholder="напр. К1 или Паркинг" value={name} onChange={e => setName(e.target.value)} />
        <span>Этажность</span><input placeholder="напр. 25" value={floors} onChange={e => setFloors(e.target.value)} />
        <span title="Этажи −1, −2, −3 — паркинг и подвалы">Подземных этажей</span>
        <input placeholder="напр. 3" value={pod} onChange={e => setPod(e.target.value)} />
      </Grid>
      <div className="pw-muted" style={{ margin: '0 0 8px' }}>Этажи 1…N создадутся сразу (1-й — тип «Первый», остальные — «Типовой»),
        подземные — с типом «{TIP_PODZEM}»: поэтажные работы на них не ложатся. Оба поля можно оставить пустыми,
        особые этажи (Тех.этаж, кровля) добавите потом кнопкой «+ доп. этаж» под колонкой.</div>
      <div className="bar" style={{ margin: 0 }}><button onClick={() => void create()}>Создать</button></div>
    </Pop>
  );
}

/** Приоритет выбранной группы работ сразу нескольким этажам (#66, 2026-09-18):
    мультивыбор на доске + одно значение; пустое поле снимает исключение. */
function PrioGruppyPop({ gruppa, kolvo, at: a, onClose, onApply }: {
  gruppa: string; kolvo: number; at: Anchor; onClose: () => void; onApply: (v: string) => Promise<void>;
}) {
  const [v, setV] = useState('');
  return (
    <Pop at={a} onClose={onClose} title={'Приоритет группы «' + gruppa + '»'}>
      <Grid>
        <span>Приоритет</span>
        <input autoFocus value={v} placeholder="больше — раньше, минус — в конец, пусто — снять"
          onChange={e => setV(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void onApply(v); }} />
      </Grid>
      <div className="pw-muted" style={{ margin: '0 0 8px' }}>
        Задастся выбранным этажам ({kolvo}) только для работ группы «{gruppa}» — он сильнее общего
        приоритета этажа. Пустое поле снимает исключение: этажи снова идут по общему приоритету.
        Даты обновятся после «Пересчитать».</div>
      <div className="bar" style={{ margin: 0 }}><button onClick={() => void onApply(v)}>Задать</button></div>
    </Pop>
  );
}

function BulkPop({ ids, at: a, onClose, onDone, onFail }: { ids: number[]; at: Anchor } & Omit<PopProps, 'ctx'>) {
  const [v, setV] = useState({ tip: '', zahv: '', prior: '', pusk: '', kont: '', kz: '', pb: '' });
  const set = (k: keyof typeof v) => (val: string) => setV(s => ({ ...s, [k]: val }));
  const selVal = (x: string) => (x === '' ? undefined : x === '__clear' ? null : x);
  const txtVal = (x: string) => { const t = x.trim(); return t === '' ? undefined : t === '-' ? null : t; };
  async function apply() {
    const body: Record<string, unknown> = { ids };
    const tip = selVal(v.tip); if (tip !== undefined) body.tip_etazha_gpr = tip;
    if (v.kz !== '') body.kontur_zakryt = v.kz === 'da';
    if (v.pb !== '') body.pusk_bylo = v.pb === 'da';
    const zahv = txtVal(v.zahv); if (zahv !== undefined) body.zahvatka = zahv;
    const prior = txtVal(v.prior); if (prior !== undefined) body.avto_prioritet = prior;
    const pusk = txtVal(v.pusk);
    if (pusk !== undefined) {
      const iso = pusk === null ? null : parseDate(pusk);
      if (iso === undefined) { onFail('Дата пуска тепла не разобрана: нужно ДД.ММ.ГГГГ'); return; }
      body.pusk_tepla = iso;
    }
    const kont = txtVal(v.kont);
    if (kont !== undefined) {
      const iso = kont === null ? null : parseDate(kont);
      if (iso === undefined) { onFail('Дата закрытия контура не разобрана: нужно ДД.ММ.ГГГГ'); return; }
      body.kontur_data = iso;
    }
    if (Object.keys(body).length === 1) { onFail('Заполните хотя бы одно поле.'); return; }
    const r = await call<{ obnovleno: number; vne_zadach: number; vernulos_zadach: number }>('post', '/api/floors/bulk-patch', body);
    if (!r.ok) { onFail(r.error.message); return; }
    onDone('Обновлено этажей: ' + r.data.obnovleno + '.'
      + (r.data.vne_zadach ? ' Исключено задач: ' + r.data.vne_zadach + '.' : '')
      + (r.data.vernulos_zadach ? ' Вернулось задач: ' + r.data.vernulos_zadach + '.' : '')
      + (r.data.vne_zadach || r.data.vernulos_zadach ? ' Нажмите «Пересчитать».' : ''));
  }
  const BulkSel = ({ value, list, onChange }: { value: string; list: string[]; onChange: (v: string) => void }) => (
    <select value={value} onChange={e => onChange(e.target.value)}>
      <option value="">— не менять —</option><option value="__clear">— очистить —</option>
      {list.filter(Boolean).map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  return (
    <Pop at={a} title={'Свойства ' + ids.length + ' выбранных этажей'} onClose={onClose}>
      <div className="pw-muted" style={{ margin: '4px 0 8px' }}>Заполненные поля применятся ко всем выбранным этажам; пустые не трогаются.
        Очистить текстовое поле — поставьте «-».</div>
      <Grid>
        <span>Тип этажа</span><BulkSel value={v.tip} list={TIPY_ETAZHA} onChange={set('tip')} />
        <span>Приоритет</span><input value={v.prior} onChange={e => set('prior')(e.target.value)} />
        <span title="Только для отчётности">Закрытие ТК, дата</span>
        <input value={v.kont} placeholder="ДД.ММ.ГГГГ" onChange={e => set('kont')(e.target.value)} />
        <span title="Отметка «случилось»: при пустой дате поставится сегодняшняя">Закрытие ТК</span>
        <select value={v.kz} onChange={e => set('kz')(e.target.value)}>
          <option value="">— не менять —</option>
          <option value="da">готово</option>
          <option value="net">не готово (план)</option>
        </select>
        <span title="Работы теплового контура не начнутся раньше этой даты">Пуск тепла, дата</span>
        <input value={v.pusk} placeholder="ДД.ММ.ГГГГ" onChange={e => set('pusk')(e.target.value)} />
        <span title="Отметка «случилось»: при пустой дате поставится сегодняшняя">Пуск тепла</span>
        <select value={v.pb} onChange={e => set('pb')(e.target.value)}>
          <option value="">— не менять —</option>
          <option value="da">готово</option>
          <option value="net">не готово (план)</option>
        </select>
      </Grid>
      <div className="bar" style={{ margin: 0 }}><button onClick={() => void apply()}>Применить</button></div>
    </Pop>
  );
}
